import styled, { keyframes } from 'styled-components';

const fadeIn = keyframes`
    from {
        opacity: 0;
        transform: translateY(10px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
`;

const spin = keyframes`
    to {
        transform: rotate(360deg);
    }
`;

export const RolesSection = styled.div`
    margin-top: 24px;
    animation: ${fadeIn} 0.4s ease-out;
`;

export const RolesHeader = styled.h3`
    font-size: 16px;
    font-weight: 600;
    color: ${({ theme }) => theme?.colors?.primary?.main || '#667eea'};
    margin: 24px 0 16px;
    padding-bottom: 8px;
    border-bottom: 1px solid ${({ theme }) => theme?.colors?.gray?.[200] || '#e2e8f0'};
    display: flex;
    align-items: center;
    gap: 8px;

    &:first-child {
        margin-top: 0;
    }

    .count {
        font-size: 14px;
        font-weight: 400;
        color: ${({ theme }) => theme?.colors?.gray?.[600] || '#718096'};
    }

    .pending-indicator {
        margin-left: 8px;
        padding: 2px 8px;
        background: #fef3c7;
        color: #92400e;
        border-radius: 4px;
        font-size: 11px;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.5px;
    }
`;

export const RolesGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
    gap: 16px;
    
    @media (max-width: 768px) {
        grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
    }
`;

export const RoleCard = styled.div`
    position: relative;
    padding: 20px 16px;
    background: white;
    border-radius: 12px;
    border: 2px solid ${props => props.$color || '#667eea'};
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
    text-align: center;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    cursor: default;
    overflow: hidden;
    opacity: ${props => {
        if (props.$isMarkedForRemoval) return 0.5;
        if (props.$isPending) return 0.7;
        return 1;
    }};

    &::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        height: 4px;
        background: ${props => props.$color || '#667eea'};
    }

    ${props => props.$isPending && `
        border-style: dashed;
        animation: pulse 2s ease-in-out infinite;
        
        @keyframes pulse {
            0%, 100% { opacity: 0.7; }
            50% { opacity: 1; }
        }
    `}

    ${props => props.$isMarkedForRemoval && `
        border-color: #e53e3e;
        background: #fff5f5;
        
        &::before {
            background: #e53e3e;
        }
    `}

    &:hover {
        transform: translateY(-4px);
        box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
        border-color: ${props => props.$color || '#667eea'};
    }

    &:active {
        transform: translateY(-2px);
    }
`;

export const RoleIconWrapper = styled.div`
    width: 40px;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: ${props => props.$color || '#667eea'};
    animation: ${fadeIn} 0.5s ease-out;
    
    svg {
        width: 100%;
        height: 100%;
        filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.1));
    }
`;

export const RoleName = styled.div`
    font-size: 14px;
    font-weight: 600;
    color: #2c3e50;
    line-height: 1.3;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 4px;

    .pending-badge {
        font-size: 10px;
        padding: 2px 8px;
        background: #48bb78;
        color: white;
        border-radius: 10px;
        font-weight: 700;
    }

    .removal-badge {
        font-size: 10px;
        padding: 2px 8px;
        background: #e53e3e;
        color: white;
        border-radius: 10px;
        font-weight: 700;
    }
`;

export const RemoveButton = styled.button`
    position: absolute;
    top: 4px;
    right: 4px;
    width: 24px;
    height: 24px;
    border-radius: 50%;
    border: none;
    background: #e53e3e;
    color: white;
    font-size: 18px;
    line-height: 1;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s;
    opacity: 0.8;

    &:hover {
        opacity: 1;
        transform: scale(1.1);
        background: #c53030;
    }

    &:active {
        transform: scale(0.95);
    }
`;

export const RoleDescription = styled.div`
    font-size: 11px;
    color: #7f8c8d;
    line-height: 1.4;
    margin-top: 4px;
`;

export const LoadingState = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 40px 20px;
    gap: 16px;

    .spinner {
        width: 40px;
        height: 40px;
        border: 4px solid #e0e0e0;
        border-top-color: #667eea;
        border-radius: 50%;
        animation: ${spin} 1s linear infinite;
    }

    p {
        color: #7f8c8d;
        font-size: 14px;
        margin: 0;
    }
`;

export const EmptyState = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 40px 20px;
    gap: 12px;

    .icon {
        width: 48px;
        height: 48px;
        opacity: 0.3;
        
        svg {
            width: 100%;
            height: 100%;
        }
    }

    p {
        color: #7f8c8d;
        font-size: 14px;
        margin: 0;
    }
`;

export const ErrorState = styled.div`
    padding: 20px;
    background: #fff5f5;
    border: 1px solid #feb2b2;
    border-radius: 8px;
    color: #c53030;
    font-size: 14px;
    text-align: center;
`;

export const AddRoleSection = styled.div`
    margin-top: 24px;
    padding-top: 24px;
    border-top: 2px dashed #e1e8ed;
`;

export const AddRoleGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
    gap: 12px;
    
    @media (max-width: 768px) {
        grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
    }
`;

export const AddRoleCard = styled.div`
    padding: 16px 12px;
    background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
    border-radius: 10px;
    border: 2px dashed ${props => props.$color || '#cbd5e0'};
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
    text-align: center;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

    &:hover {
        transform: translateY(-2px);
        border-style: solid;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    }
`;

export const AddButton = styled.button`
    margin-top: 4px;
    padding: 6px 12px;
    background: ${props => props.$color || '#667eea'};
    color: white;
    border: none;
    border-radius: 6px;
    font-size: 11px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s;
    width: 100%;

    &:hover {
        transform: scale(1.05);
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
    }

    &:active {
        transform: scale(0.98);
    }
`;

export const ActionsBar = styled.div`
    display: flex;
    gap: 8px;
    align-items: center;
    margin-left: auto;
`;

export const SaveButton = styled.button`
    padding: 6px 16px;
    background: ${({ theme }) => theme?.colors?.success?.main || '#48bb78'};
    color: white;
    border: none;
    border-radius: 6px;
    font-size: 13px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s;
    display: flex;
    align-items: center;
    gap: 6px;
    white-space: nowrap;

    &:hover:not(:disabled) {
        background: ${({ theme }) => theme?.colors?.success?.dark || '#38a169'};
        transform: translateY(-1px);
        box-shadow: 0 2px 8px rgba(72, 187, 120, 0.25);
    }

    &:disabled {
        background: #cbd5e0;
        cursor: not-allowed;
        transform: none;
        opacity: 0.6;
    }

    &:active:not(:disabled) {
        transform: translateY(0);
    }
`;

export const CancelButton = styled.button`
    padding: 6px 16px;
    background: transparent;
    color: #718096;
    border: 1px solid #cbd5e0;
    border-radius: 6px;
    font-size: 13px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s;
    white-space: nowrap;

    &:hover {
        background: #f7fafc;
        border-color: #a0aec0;
        color: #4a5568;
    }

    &:active {
        transform: scale(0.98);
    }
`;
